import { EditUnifiedFieldDrawer } from '@modules/ManageUnifiedFields/components/EditUnifiedFieldDrawer';
import { AutoCompleteTextField } from '@modules/common/components/AutoCompleteTextField';
import { EqlFieldCommonMetadataSourceIcons } from '@modules/common/components/EqlFieldCommonMetadataInfo';
import {
  getFormattedDisplayNameForEqlField,
  getGroupLabelFromEqlField,
} from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import { UNIFIED_FIELD } from '@modules/common/constants';
import {
  useFetchEQLFields,
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
  useShouldShowEQLFieldTooltipInQB,
} from '@modules/common/hooks';
import { trackCustomFilterEvents } from '@modules/queryBuilder/helpers';
import { useCanEditCustomFilter } from '@modules/queryBuilder/hooks';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { EditRounded, InfoOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteOwnerState,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Box,
  IconButton,
  Paper,
  TextFieldProps,
  Tooltip,
  Typography,
  useControlled,
} from '@mui/material';
import { EqlField, EqlFieldValueType } from 'generated/graphql';
import { HTMLAttributes, SyntheticEvent, useState } from 'react';
import { EQLGroupFieldDescriptionTooltipContent } from '../EQLGroupFieldDescriptionTooltipContent';
import { EditCustomFilterModal } from '../EditCustomFilterModal';
import { EqlFieldCommonMetadataEditButton } from '../EqlFieldCommonMetadataInfo/EqlFieldCommonMetadataEditIcon';
import { EqlFieldsAutoCompleteOption } from '../EqlFieldsAutoCompleteOption';
import { FieldDetailsTooltipContent } from '../FieldDetailsTooltipContent';
import { useMuiModalContext } from '../Modal/ModalContext';

export interface EqlFieldsAutoCompleteProps
  extends Omit<
    AutocompleteProps<EqlField, false, false, false>,
    'renderInput' | 'options'
  > {
  groupID?: string;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  options?: EqlField[];
  getOptionDisplayName?: (option: EqlField) => string;
  inputVariant?: TextFieldProps['variant'];
  shouldExcludeCustomFilters?: boolean;
}

export function EqlFieldsAutoComplete(props: EqlFieldsAutoCompleteProps) {
  const defaultRenderInput = (params: AutocompleteRenderInputParams) => (
    <AutoCompleteTextField
      {...params}
      placeholder="Select a field"
      variant={props.inputVariant}
      open={props.open}
    />
  );

  const [shouldOpenUnifiedFieldDrawer, setShouldOpenUnifiedFieldDrawer] =
    useState(false);
  const [selectedUnifiedFieldForEdit, setSelectedUnifiedFieldForEdit] =
    useState<EqlField | undefined>();

  const shouldShowFieldDetailsTooltip = useShouldShowEQLFieldTooltipInQB();

  const canEditCustomFilter = useCanEditCustomFilter();

  const { fields, isLoading: isLoadingEQLFields } = useFetchEQLFields({
    groupID: props.groupID,
    fetchPolicy: props.groupID ? 'cache-and-network' : 'cache-first', // At this point, we already have latest fetched fields in cache for non-groupID call
  });

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const {
    renderInput = defaultRenderInput,
    onClose,
    inputValue: inputValueProp,
    onInputChange: onInputChangeProp,
    popupIcon = false,
    options = fields,
    getOptionLabel = (option) =>
      getFormattedDisplayNameForEqlField({
        eqlField: option,
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      }),
    getOptionDisplayName = (option) => option.displayName,
    groupBy = (option) =>
      getGroupLabelFromEqlField({
        eqlField: option,
        shouldRemoveFileUploadPrefix,
      }),
    loading,
    shouldExcludeCustomFilters = false,
    componentsProps = {},
    renderGroup: renderGroupProp,
    renderOption: renderOptionProp,
    ...autoCompleteProps
  } = props;

  const [inputValue, setInputValue] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: 'EqlFieldsAutoComplete',
    state: 'inputValue',
  });

  const onInputChange = (
    eve: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setInputValue(value);
    onInputChangeProp?.(eve, value, reason);
  };

  const { openModal, closeModal } = useMuiModalContext();

  const openEditCustomFilterModal = (option: EqlField) => {
    openModal({
      contentComponent: () => (
        <QueryBuilderStoreProvider>
          <EditCustomFilterModal
            onClose={closeModal}
            filterName={option.displayName}
            filterID={option.detail?.reference?.referenceID}
          />
        </QueryBuilderStoreProvider>
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'md',
      },
    });
    trackCustomFilterEvents({
      eventName: 'Custom Filter Edit Button Clicked',
      filterName: option.displayName ?? '',
    });
  };

  const renderSecondaryAction = (option: EqlField) => {
    if (option.valueType === EqlFieldValueType.None) {
      if (!canEditCustomFilter(option.detail?.reference?.creatorID)) {
        return null;
      }

      return (
        <Box
          className="EditIconContainer"
          sx={{ opacity: 0 }}
          onClick={(event) => event.stopPropagation()}
        >
          <Tooltip title="Edit Filter" disableInteractive>
            <IconButton onClick={() => openEditCustomFilterModal(option)}>
              <EditRounded fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <>
        <EqlFieldCommonMetadataSourceIcons
          eqlField={option}
          tooltipPlacement="bottom"
        />
        <EqlFieldCommonMetadataEditButton
          eqlField={option}
          setShouldOpenUnifiedFieldDrawer={setShouldOpenUnifiedFieldDrawer}
          setSelectedUnifiedFieldForEdit={setSelectedUnifiedFieldForEdit}
        />
      </>
    );
  };

  const optionsWithoutCustomFilter = shouldExcludeCustomFilters
    ? options.filter((option) => option.valueType !== EqlFieldValueType.None)
    : options;

  const renderAutoCompleteOptionTooltip = (field: EqlField) => {
    const shouldShowTooltip = shouldShowFieldDetailsTooltip(field);

    if (!shouldShowTooltip) {
      return null;
    }
    return <FieldDetailsTooltipContent field={field} />;
  };

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    if (renderGroupProp) {
      return renderGroupProp(params);
    }

    if (!params.group && params.children) {
      return (
        <Box component="li" key={params.key} sx={{ m: 0, p: 0 }}>
          {params.children}
        </Box>
      );
    }

    const canRenderCustomGroup = params.group === UNIFIED_FIELD;

    return (
      <Box component="li" key={params.key} sx={{ m: 0, p: 0 }}>
        <Paper elevation={0} sx={{ px: 2, py: 1.5, borderRadius: 0, my: 0.5 }}>
          <Typography variant="body2" color="text.secondary" component={Box}>
            {params.group}
            {canRenderCustomGroup && (
              <Tooltip
                title={
                  <EQLGroupFieldDescriptionTooltipContent
                    groupID={params.group}
                    displayName={params.group}
                  />
                }
              >
                <InfoOutlined fontSize="inherit" sx={{ ml: 1 }} />
              </Tooltip>
            )}
          </Typography>
        </Paper>
        {params.children}
      </Box>
    );
  };

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement> & {
      key: string;
    },
    option: EqlField,
    state: AutocompleteRenderOptionState,
    ownerState: AutocompleteOwnerState<EqlField, false, false, false>,
  ) => {
    if (renderOptionProp) {
      return renderOptionProp(props, option, state, ownerState);
    }

    return (
      <EqlFieldsAutoCompleteOption
        inputValue={inputValue}
        getOptionDisplayName={getOptionDisplayName}
        props={props}
        option={option}
        renderSecondaryAction={renderSecondaryAction}
        renderTooltipTitle={() => renderAutoCompleteOptionTooltip(option)}
      />
    );
  };

  return (
    <>
      <Autocomplete
        {...autoCompleteProps}
        value={props.value ?? null} // Empty state needs to be null for Autocomplete, undefined causes issue in controlled mode
        fullWidth
        clearOnBlur
        autoHighlight
        loading={loading || isLoadingEQLFields}
        options={optionsWithoutCustomFilter}
        getOptionLabel={getOptionLabel}
        popupIcon={popupIcon}
        inputValue={inputValue}
        onInputChange={onInputChange}
        renderInput={renderInput}
        renderOption={renderOption}
        renderGroup={renderGroup}
        clearIcon={null}
        componentsProps={{ paper: { elevation: 8 }, ...componentsProps }}
        isOptionEqualToValue={(option, value) => option.field === value?.field}
        onClose={(eve, reason) => {
          if (reason !== 'toggleInput') {
            onClose?.(eve, reason);
          }
          if (reason !== 'toggleInput' && reason !== 'selectOption') {
            setInputValue('');
          }
        }}
        groupBy={groupBy}
      />
      {selectedUnifiedFieldForEdit?.field && (
        <EditUnifiedFieldDrawer
          open={shouldOpenUnifiedFieldDrawer}
          metadataKey={selectedUnifiedFieldForEdit.field}
          onClose={() => {
            setShouldOpenUnifiedFieldDrawer(false);
            setSelectedUnifiedFieldForEdit(undefined);
          }}
        />
      )}
    </>
  );
}
